import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Plus,
  Timer,
  Clock,
  Edit2,
  XCircle,
  UserPlus,
  Trash2,
  RotateCcw,
  Loader2,
  ArrowRightLeft,
  AlertTriangle,
  CalendarDays,
} from "lucide-react";
import { businessApi } from "../../../services/api";
import DatePickerInput from "../../../components/DatePickerInput";
import { useBusinessAuth } from "../../context/BusinessAuthContext";
import ShopSelect from "../../../components/ShopSelect";
import { groupShiftAssignments } from "./shiftAssignmentView";
import { shiftDeliveryAdvice } from "./shiftDeliveryAdvice";
import { WEEKDAYS, defaultWeek, describeWeek, sameWeek, weekFromAssignment, workDayCount } from "./scheduleDays";
import { hoursText, validateShiftForm } from "./shiftFormValidation";
import { getBusinessDispatchWindows } from "../../services/businessNotificationService";
import RequestReviewList from "./components/RequestReviewList";
import { Button, ConfirmDialog, Dialog, Notice, PageHeader, SearchInput, Status, plainError, Th, THead, useSelectedTabInView } from "../../../components/ui";
import { focusRing } from "../../../components/ui/cn";
import { describeScheduleRequest } from "./myHrText";
import { SCHEDULED_STATE_LABEL, describeScheduledInterval, describeScheduledPeriod, isLiveScheduled } from "./scheduledOvertimeText";
import ProposedScheduleFit from "./components/ProposedScheduleFit";
import ScheduledOvertimePanel, { ScheduleOvertimeDialog } from "./components/ScheduledOvertimePanel";

const SHIFT_TABS = [
  { key: "shifts", label: "Shift definitions" },
  { key: "assignments", label: "Employee schedules" },
  { key: "staffing", label: "Staffing" },
  { key: "requests", label: "Requests" },
  // Report 70 hardening (APP-HR-153): scheduled overtime is planned beside the weekly schedule, so a shift planner who
  // may propose overtime does it here without needing attendance access. Same component and API as HR › Attendance.
  { key: "overtime", label: "Scheduled overtime" },
];

// Status as a word: only "Active" is worth the reader's eye here, so the rest is secondary text.
function StatusBadge({ status }) {
  const active = String(status || "").toLowerCase() === "active";
  return <span className={active ? "text-foreground" : "text-secondary"}>{active ? "Active" : "Inactive"}</span>;
}

function getTodayInputValue(now = new Date()) {
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function normalizeDateInputValue(value) {
  if (!value) return "";
  const match = String(value).trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
}

function formatDateValue(value) {
  if (!value) return "Present";
  const normalized = normalizeDateInputValue(value);
  if (!normalized) return String(value);
  const parsed = new Date(`${normalized}T00:00:00`);
  return Number.isNaN(parsed.getTime())
    ? normalized
    : parsed.toLocaleDateString("en-PH", { year: "numeric", month: "short", day: "numeric" });
}

function isFutureDate(value, today = getTodayInputValue()) {
  const normalized = normalizeDateInputValue(value);
  return Boolean(normalized && normalized > today);
}

function formatMemberStatusLabel(status) {
  return String(status || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function MemberStatusBadge({ status }) {
  const normalized = String(status || "").toLowerCase();
  return <span className={`text-meta ${normalized === "active" ? "text-secondary" : "font-medium text-warning"}`}>{formatMemberStatusLabel(normalized || "unknown")}</span>;
}

function buildMemberOptionLabel(member) {
  const baseName = member?.name || member?.member_name || member?.memberName || member?.email || `#${member?.id}`;
  const bits = [baseName];
  if (member?.employeeCode) bits.push(member.employeeCode);
  if (member?.status) bits.push(formatMemberStatusLabel(member.status));
  if (member?.currentShiftName) bits.push("Has a weekly schedule");
  return bits.join(" • ");
}

/* ─── Shift Form Modal ──────────────────────────────────── */
function fmtTime(value) {
  if (!value) return "-";
  const parts = String(value).split(":");
  const hour = Number(parts[0]);
  const minute = parts[1] || "00";
  const am = hour < 12 ? "AM" : "PM";
  return `${hour % 12 || 12}:${minute} ${am}`;
}

function shiftNamer(shifts = []) {
  const byId = new Map(shifts.map((item) => [Number(item.id), item.name]));
  return (id) => byId.get(Number(id)) || `Shift #${id}`;
}

// Report 70 hardening (P0-A): a shift whose paid time is longer than the normal hours of a day (an older shift with
// no break recorded) cannot go into a NEW schedule; the server refuses it too. Employees already on it keep it.
export const isLongShift = (shift) => Boolean(shift?.paid_time?.notes?.includes("PAID_TIME_ABOVE_NORMAL_HOURS"));

export function longShiftInWeek(week = {}, shifts = []) {
  const ids = new Set(WEEKDAYS.map(({ day }) => Number(week?.[day])).filter(Boolean));
  return shifts.find((item) => ids.has(Number(item.id)) && isLongShift(item)) || null;
}

export function longShiftMessage(shift) {
  const time = shift?.paid_time || {};
  const normal = hoursText(time.normalDayMinutes || 480);
  return `${shift?.name || "This shift"} pays ${hoursText(time.compensableMinutes)} a day${time.breakMinutes ? "" : " (no unpaid break recorded)"}, more than an ordinary ${normal} day, so it can't be used in a new schedule. Pick a shift that records the unpaid meal break or ends within ${normal}, and plan any extra hours as scheduled overtime.`;
}

// Each weekday: one shift or Rest (owner decision "Scheduling Option B"). One row per
// weekday, so a day can never hold two shifts.
function WeeklyScheduleEditor({ value = {}, onChange, shifts = [], idPrefix }) {
  const active = shifts.filter((item) => item.status === "active");
  const options = [
    { value: "", label: "Rest" },
    ...active.map((item) => ({
      value: String(item.id),
      disabled: isLongShift(item),
      label: `${item.name} (${fmtTime(item.start_time)} – ${fmtTime(item.end_time)}${Number(item.is_overnight) ? ", ends next day" : ""}${item.paid_time ? ` · ${hoursText(item.paid_time.compensableMinutes)} paid` : ""})${isLongShift(item) ? " · over 8 paid hours, not available" : ""}`,
    })),
  ];
  const longShift = longShiftInWeek(value, shifts);
  const nameOf = shiftNamer(shifts);
  const works = workDayCount(value) > 0;
  // Report 55 (OD-26): the server refuses a week with no rest day; said here before saving.
  const rests = workDayCount(value) < WEEKDAYS.length;
  // Report 58: the week's scheduled paid time, from each day's shift (the server's paid-time figure for that shift).
  const paidOf = (id) => shifts.find((item) => Number(item.id) === Number(id))?.paid_time?.compensableMinutes ?? null;
  const weekPaid = WEEKDAYS.reduce((total, { day }) => total + (value[day] ? Number(paidOf(value[day]) || 0) : 0), 0);
  return (
    <fieldset className="mb-3 min-w-0" aria-describedby={`${idPrefix}-week-hint`}>
      <legend className="mb-1 block text-body-sm font-medium">Weekly schedule *</legend>
      <div className="divide-y divide-subtle rounded-panel border border-default">
        {WEEKDAYS.map(({ day, long }) => (
          <div key={day} className="flex items-center gap-3 px-3 py-1.5">
            <span className="w-20 shrink-0 text-body-sm sm:w-24">{long}</span>
            <div className="min-w-0 flex-1">
              <ShopSelect
                value={value[day] ? String(value[day]) : ""}
                onValueChange={(next) => onChange({ ...value, [day]: next ? Number(next) : null })}
                options={options}
                ariaLabel={`${long} shift`}
                align="start"
                minPanelWidth={220}
                triggerClassName="min-h-9 w-full rounded-control px-3 py-1.5 text-body-sm"
                panelClassName="py-1"
              />
            </div>
            <span className="w-14 shrink-0 text-right text-meta tabular-nums text-secondary">{value[day] && paidOf(value[day]) != null ? hoursText(paidOf(value[day])) : value[day] ? "" : "Rest"}</span>
          </div>
        ))}
      </div>
      {works ? (
        <p className="mt-1 text-meta text-secondary">
          {workDayCount(value)} work day{workDayCount(value) === 1 ? "" : "s"} · {hoursText(weekPaid)} of scheduled paid time a week · rest on {WEEKDAYS.filter(({ day }) => !value[day]).map(({ long }) => long).join(", ") || "no day"}.
        </p>
      ) : null}
      <p id={`${idPrefix}-week-hint`} className={`mt-1 text-meta ${works && rests ? "text-secondary" : "text-danger"}`}>
        {!works ? "Choose at least one work day." : !rests ? "Choose at least one rest day. An employee must have 24 hours of rest after six work days in a row." : describeWeek(value, nameOf)}
      </p>
      {longShift ? (
        <p role="alert" className="mt-2 flex items-start gap-1.5 rounded-control bg-danger-subtle p-2.5 text-meta text-danger" data-testid={`${idPrefix}-long-shift`}>
          <AlertTriangle aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{longShiftMessage(longShift)}</span>
        </p>
      ) : null}
    </fieldset>
  );
}

function toFormTime(value, fallback = "") {
  if (!value) return fallback;
  return String(value).slice(0, 5);
}

function shiftToForm(initial) {
  if (!initial) {
    return { name: "", code: "", start_time: "08:00", end_time: "17:00", break_start_time: "12:00", break_minutes: "60", break_paid: false };
  }
  return {
    ...initial,
    start_time: toFormTime(initial.start_time),
    end_time: toFormTime(initial.end_time),
    break_start_time: toFormTime(initial.break_start_time),
    break_minutes: String(initial.break_minutes ?? 0),
    break_paid: Number(initial.break_paid || 0) === 1,
  };
}

function FieldError({ id, message }) {
  if (!message) return null;
  return <p id={id} className="mt-1 text-meta text-danger">{message}</p>;
}

function ShiftFormModal({ open, onClose, onSubmit, initial, defaultGrace, normalDayMinutes = 480 }) {
  const [form, setForm] = useState(() => shiftToForm(initial));
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) { setForm(shiftToForm(initial)); setTouched(false); }
  }, [open, initial]);

  if (!open) return null;
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const editing = !!initial?.id;
  // APP-HR-084: once a shift has been used, its times belong to the schedules and attendance that used it. They are
  // shown, not editable; the server refuses the change whatever the form sends. The name and code stay editable.
  const timesLocked = editing && Number(initial?.is_used) === 1;
  // Report 58: a used shift's times are fixed, so its paid-time rules are not re-checked here (the server refuses changes).
  const validated = validateShiftForm(form, { normalDayMinutes });
  const { overnight, windowMinutes, unpaidBreak, paidMinutes } = validated;
  const errors = timesLocked ? Object.fromEntries(Object.entries(validated.errors).filter(([key]) => key === "name")) : validated.errors;
  const shown = touched ? errors : {};
  const ownGrace = initial?.grace_period_minutes !== null && initial?.grace_period_minutes !== undefined ? Number(initial.grace_period_minutes) : null;
  const inputCls = "h-control w-full rounded-control border border-default bg-surface px-3 text-body text-foreground placeholder:text-tertiary hover:border-strong focus:border-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-touch";

  const handleSubmit = async () => {
    setTouched(true);
    if (saving || Object.keys(errors).length) return;
    setSaving(true);
    const breakMinutes = form.break_minutes === "" ? 0 : Number(form.break_minutes);
    try {
      await onSubmit({
        name: form.name.trim(),
        code: form.code?.trim() || null,
        start_time: form.start_time,
        end_time: form.end_time,
        break_minutes: breakMinutes,
        break_start_time: breakMinutes > 0 ? form.break_start_time : null,
        break_paid: breakMinutes > 0 && Boolean(form.break_paid),
      });
    } finally { setSaving(false); }
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => { if (!next && !saving) onClose(); }}
      title={editing ? "Edit shift" : "New shift"}
      size="md"
      footer={(
        <>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" form="shift-form" variant="primary" loading={saving}>
            {editing ? "Save changes" : "Create shift"}
          </Button>
        </>
      )}
    >
      <form id="shift-form" onSubmit={(event) => { event.preventDefault(); handleSubmit(); }} className="text-body-sm text-foreground">

        <label htmlFor="shift-name" className="mb-1 block text-body-sm font-medium">Name *</label>
        <input id="shift-name" value={form.name} onChange={set("name")} maxLength={100} placeholder="e.g. Morning Shift" aria-invalid={Boolean(shown.name)} aria-describedby="shift-name-error" className={inputCls} />
        <FieldError id="shift-name-error" message={shown.name} />

        <label htmlFor="shift-code" className="mb-1 mt-3 block text-body-sm font-medium">Code</label>
        <input id="shift-code" value={form.code || ""} onChange={set("code")} maxLength={20} placeholder="e.g. AM" className={inputCls} />

        {timesLocked ? (
          <Notice className="mt-3" title="The times of this shift are fixed">
            It is already on employees' schedules or attendance. To change the hours, create a new shift and move employees to it from a date.
          </Notice>
        ) : null}

        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="shift-start" className="mb-1 block text-body-sm font-medium">Start *</label>
            <input id="shift-start" type="time" value={form.start_time} onChange={set("start_time")} disabled={timesLocked} aria-invalid={Boolean(shown.start_time)} aria-describedby="shift-start-error" className={inputCls} />
            <FieldError id="shift-start-error" message={shown.start_time} />
          </div>
          <div>
            <label htmlFor="shift-end" className="mb-1 block text-body-sm font-medium">End *</label>
            <input id="shift-end" type="time" value={form.end_time} onChange={set("end_time")} disabled={timesLocked} aria-invalid={Boolean(shown.end_time)} aria-describedby="shift-end-error" className={inputCls} />
            <FieldError id="shift-end-error" message={shown.end_time} />
          </div>
        </div>
        {overnight ? <p className="mt-1 text-meta text-warning">Ends the next day (overnight shift).</p> : null}

        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="shift-break-start" className="mb-1 block text-body-sm font-medium">Break starts</label>
            <input id="shift-break-start" type="time" value={form.break_start_time || ""} onChange={set("break_start_time")} disabled={timesLocked || Number(form.break_minutes || 0) === 0} aria-invalid={Boolean(shown.break_start_time)} aria-describedby="shift-break-start-error" className={`${inputCls} disabled:opacity-50`} />
            <FieldError id="shift-break-start-error" message={shown.break_start_time} />
          </div>
          <div>
            <label htmlFor="shift-break-minutes" className="mb-1 block text-body-sm font-medium">Break (min)</label>
            <input id="shift-break-minutes" type="number" min={0} max={240} step={5} value={form.break_minutes} onChange={set("break_minutes")} disabled={timesLocked} aria-invalid={Boolean(shown.break_minutes)} aria-describedby="shift-break-minutes-error" className={inputCls} />
            <FieldError id="shift-break-minutes-error" message={shown.break_minutes} />
          </div>
        </div>
        <label className="mt-2 inline-flex min-h-touch items-center gap-2 text-body-sm md:min-h-0">
          <input type="checkbox" className="h-4 w-4" checked={Boolean(form.break_paid)} disabled={timesLocked || Number(form.break_minutes || 0) === 0}
            onChange={(event) => setForm((f) => ({ ...f, break_paid: event.target.checked }))} />
          The break is paid working time
        </label>
        <p className="mt-1 text-meta text-secondary">
          Employees do not clock out for the break. An unpaid meal break is not paid time; a short rest break (under 20 minutes) is always paid. Use 0 for no break.
        </p>

        {windowMinutes > 0 ? (
          <div className="mt-3 rounded-control border border-default bg-surface-sunken px-3 py-2 text-body-sm" aria-live="polite">
            <span className="block text-foreground">Paid time a day: <span className="font-medium tabular-nums">{hoursText(paidMinutes)}</span></span>
            <span className="block text-meta text-secondary">
              {fmtTime(form.start_time)} – {fmtTime(form.end_time)}{overnight ? " (next day)" : ""} is {hoursText(windowMinutes)}
              {unpaidBreak ? `, less an unpaid break of ${hoursText(unpaidBreak)}` : Number(form.break_minutes || 0) > 0 ? ", the break is paid" : ", no break"}.
              Time after the end counts only as approved overtime.
            </span>
          </div>
        ) : null}

        <div className="mt-3 text-meta text-secondary">
          {ownGrace !== null
            ? `This older shift keeps its own lateness grace of ${ownGrace} minutes. New shifts follow the attendance pay policy.`
            : `Lateness grace comes from the attendance pay policy the owner sets (now ${defaultGrace ?? "—"} minutes), the same for every shift.`}
        </div>
      </form>
    </Dialog>
  );
}

/* ─── Assign Modal ──────────────────────────────────────── */
// F9: warn, never block. Only shown for Riders, when the store's receiving hours
// are known; other employees' shifts are HR's alone.
function DeliveryAdvice({ advice }) {
  if (!advice?.length) return null;
  return (
    <div className="mb-3 space-y-1 rounded-control border border-default bg-surface-sunken p-3 text-meta text-secondary" data-testid="shift-delivery-advice">
      <p className="font-semibold">Delivery trips for this Rider</p>
      {advice.map((line) => <p key={line}>{line}</p>)}
      <p className="opacity-80">Delivery advice only: it does not stop the save.</p>
    </div>
  );
}

function AssignModal({ open, onClose, onSubmit, shift, shifts = [], members, defaults, adviceFor = () => [] }) {
  const [memberId, setMemberId] = useState("");
  const [week, setWeek] = useState(() => defaultWeek(shift?.id, defaults?.workWeekdays));
  const [effectiveFrom, setEffectiveFrom] = useState(getTodayInputValue());
  const [effectiveUntil, setEffectiveUntil] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setMemberId("");
      setWeek(defaultWeek(shift?.id, defaults?.workWeekdays));
      setEffectiveFrom(getTodayInputValue());
      setEffectiveUntil("");
      setReason("");
      setSaving(false);
    }
  }, [open, defaults, shift]);

  if (!open) return null;
  const selectedMember = members.find((member) => String(member.id) === String(memberId));

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose(); }} title="Assign weekly schedule" size="lg">
      <div className="text-body-sm text-foreground">
        <p className="mb-4 text-body-sm text-secondary">
          Starts from {shift?.name} on the business's usual work days. Set each day to a shift or Rest; the week repeats from the start date on.
        </p>

        <label className="mb-1 block text-body-sm font-medium">Employee *</label>
        <ShopSelect
          value={memberId}
          onValueChange={setMemberId}
          options={[
            { value: "", label: "— Select —" },
            ...members.map((member) => ({ value: member.id, label: buildMemberOptionLabel(member) })),
          ]}
          ariaLabel="Employee"
          align="start"
          minPanelWidth={240}
          triggerClassName="mb-3 min-h-10 w-full rounded-control px-3 py-2 text-body-sm"
          panelClassName="py-1"
        />
        {selectedMember?.currentShiftName ? (
          <p className="mb-3 rounded-control bg-warning-subtle p-3 text-meta text-warning" data-testid="assign-current-shift">
            {selectedMember.name} already has a weekly schedule since {formatDateValue(selectedMember.currentShiftEffectiveFrom)}{selectedMember.currentShiftEffectiveUntil ? ` until ${formatDateValue(selectedMember.currentShiftEffectiveUntil)}` : " with no end date"}.
            {" "}To change it, use Change weekly schedule on the Assignments tab. Assigning here only works for dates outside that schedule.
          </p>
        ) : null}
        <DeliveryAdvice advice={memberId ? adviceFor(memberId, shift) : []} />

        <div className="mb-3 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-body-sm font-medium">From *</label>
            <DatePickerInput value={effectiveFrom} onValueChange={setEffectiveFrom} required title="SELECT EFFECTIVE DATE" ariaLabel="Assignment effective from" className="w-full rounded-control border px-3 py-2 text-body-sm" />
          </div>
          <div>
            <label className="mb-1 block text-body-sm font-medium">Until</label>
            <DatePickerInput value={effectiveUntil} onValueChange={setEffectiveUntil} min={effectiveFrom} title="SELECT END DATE" ariaLabel="Assignment effective until" className="w-full rounded-control border px-3 py-2 text-body-sm" />
          </div>
        </div>

        <WeeklyScheduleEditor value={week} onChange={setWeek} shifts={shifts} idPrefix="assign" />

        <label className="mb-1 block text-body-sm font-medium">Reason</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="Optional note for why this schedule was assigned"
          className="mb-4 w-full rounded-control border px-3 py-2 text-body-sm"
        />

        {longShiftInWeek(week, shifts) ? (
          <p className="mb-4 text-meta text-danger">Choose shifts of at most 8 paid hours above; the pay check and Save follow.</p>
        ) : <ProposedScheduleFit memberId={memberId} week={week} effectiveFrom={normalizeDateInputValue(effectiveFrom)} />}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-control border px-4 py-2 text-body-sm">Cancel</button>
          <button
            onClick={async () => {
              if (saving) return;
              setSaving(true);
              try {
                await onSubmit({
                  business_member_id: Number(memberId),
                  weekly_schedule: week,
                  effective_from: normalizeDateInputValue(effectiveFrom),
                  effective_until: normalizeDateInputValue(effectiveUntil) || undefined,
                  reason: reason.trim() || undefined,
                });
              } finally {
                setSaving(false);
              }
            }}
            disabled={!memberId || !effectiveFrom || !workDayCount(week) || workDayCount(week) === WEEKDAYS.length || Boolean(longShiftInWeek(week, shifts)) || saving}
            className="business-primary-action inline-flex items-center gap-2 rounded-control bg-accent px-4 py-2 text-body-sm text-on-accent disabled:opacity-40"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Assign schedule
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function ReassignModal({ open, onClose, onSubmit, assignment, shifts, initialShiftId = null, adviceFor = () => [] }) {
  const today = getTodayInputValue();
  const assignmentEffectiveFrom = normalizeDateInputValue(assignment?.effective_from);
  const assignmentEffectiveUntil = normalizeDateInputValue(assignment?.effective_until);
  const initialFrom =
    assignmentEffectiveFrom && assignmentEffectiveFrom > today
      ? assignmentEffectiveFrom
      : today;

  const currentWeek = weekFromAssignment(assignment);
  const [week, setWeek] = useState(currentWeek);
  const [effectiveFrom, setEffectiveFrom] = useState(initialFrom);
  const [effectiveUntil, setEffectiveUntil] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !assignment) return;
    const nextFrom =
      assignmentEffectiveFrom && assignmentEffectiveFrom > today
        ? assignmentEffectiveFrom
        : today;
    // Coming from "already has a shift" on Assign: move the same work days to the chosen shift.
    const base = weekFromAssignment(assignment);
    setWeek(initialShiftId
      ? Object.fromEntries(WEEKDAYS.map(({ day }) => [day, base[day] ? Number(initialShiftId) : null]))
      : base);
    setEffectiveFrom(nextFrom);
    setEffectiveUntil(assignmentEffectiveUntil || "");
    setReason("");
    setSaving(false);
  }, [assignment, assignmentEffectiveFrom, assignmentEffectiveUntil, initialShiftId, open, today]);

  if (!open || !assignment) return null;
  const nameOf = shiftNamer(shifts);
  const unchanged = sameWeek(week, currentWeek);
  const firstShift = shifts.find((item) => WEEKDAYS.some(({ day }) => Number(week[day]) === Number(item.id)));

  const handleSubmit = async () => {
    if (saving) return;

    setSaving(true);
    try {
      await onSubmit({
        weekly_schedule: week,
        effective_from: normalizeDateInputValue(effectiveFrom),
        effective_until: normalizeDateInputValue(effectiveUntil) || undefined,
        reason: reason.trim() || undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  const isTodayChange = effectiveFrom === today;
  const hasStarted = Boolean(assignmentEffectiveFrom && assignmentEffectiveFrom < today);

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose(); }} title="Change weekly schedule" size="lg">
      <div className="text-body-sm text-foreground">
        <p className="mb-4 text-body-sm text-secondary">
          A lasting change: the weekly schedule changes from the date you pick, and earlier days keep their schedule in history. For one day or a few days only, use Temporary change.
        </p>

        <div className="mb-4 grid gap-3 md:grid-cols-2">
          <div className="rounded-panel border border-default p-4">
            <div className="text-meta font-semibold text-tertiary">Employee</div>
            <div className="mt-1 text-body-sm font-medium">{assignment.member_name || `#${assignment.business_member_id}`}</div>
            <div className="mt-3 text-meta font-semibold text-tertiary">Current weekly schedule</div>
            <div className="mt-1 text-body-sm">{describeWeek(currentWeek, nameOf)}</div>
            <div className="mt-3 text-meta font-semibold text-tertiary">Current range</div>
            <div className="mt-1 text-body-sm">{formatDateValue(assignment.effective_from)} - {formatDateValue(assignment.effective_until)}</div>
          </div>

          <div className="rounded-panel border border-warning bg-warning-subtle p-4 text-body-sm text-warning">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="space-y-2">
                <p>Future-dated changes are safest. Historical changes are blocked.</p>
                {isTodayChange && <p>Same-day changes are blocked if attendance already exists or the employee is currently clocked in.</p>}
                {hasStarted && <p>This assignment already started. The change starts on the effective date; the days before it keep their old shift in history.</p>}
                {String(assignment.member_status || "").toLowerCase() === "on_hold" && (
                  <p>On-hold members can only receive future-dated shift changes.</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <WeeklyScheduleEditor value={week} onChange={setWeek} shifts={shifts} idPrefix="reassign" />
        <DeliveryAdvice advice={firstShift ? adviceFor(assignment.business_member_id, firstShift) : []} />

        <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-body-sm font-medium">Effective from *</label>
            <DatePickerInput
              value={effectiveFrom}
              onValueChange={setEffectiveFrom}
              min={today}
              title="SELECT EFFECTIVE DATE"
              ariaLabel="Shift effective from"
              className="w-full rounded-control border px-3 py-2 text-body-sm"
            />
          </div>
        </div>

        <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-body-sm font-medium">Effective until</label>
            <DatePickerInput
              value={effectiveUntil}
              onValueChange={setEffectiveUntil}
              min={effectiveFrom || today}
              title="SELECT END DATE"
              ariaLabel="Shift effective until"
              className="w-full rounded-control border px-3 py-2 text-body-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-body-sm font-medium">Reason</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Optional note for audit history"
              className="w-full rounded-control border px-3 py-2 text-body-sm"
            />
          </div>
        </div>

        {longShiftInWeek(week, shifts) ? (
          <p className="mb-4 text-meta text-danger">Choose shifts of at most 8 paid hours above; the pay check and Save follow.</p>
        ) : <ProposedScheduleFit memberId={assignment.business_member_id} week={week} effectiveFrom={normalizeDateInputValue(effectiveFrom)} />}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-control border px-4 py-2 text-body-sm">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={!effectiveFrom || !workDayCount(week) || workDayCount(week) === WEEKDAYS.length || unchanged || Boolean(longShiftInWeek(week, shifts)) || saving}
            className="business-primary-action inline-flex items-center gap-2 rounded-control bg-accent px-4 py-2 text-body-sm text-on-accent disabled:opacity-40"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Change weekly schedule
          </button>
        </div>
      </div>
    </Dialog>
  );
}

function WeeklySummary({ assignment, nameOf, className = "text-body-sm" }) {
  return <div className={className}>{describeWeek(weekFromAssignment(assignment), nameOf)}</div>;
}

/** Report 55 (OD-26): a weekly schedule that works all seven days has no rest day; payroll holds that employee. */
export function hasNoRestDay(assignment) {
  if (!assignment) return false;
  return workDayCount(weekFromAssignment(assignment)) === WEEKDAYS.length;
}

function NoRestDay({ assignment }) {
  if (!hasNoRestDay(assignment)) return null;
  return (
    <p className="mt-1 text-meta">
      <Status tone="warning" icon>No rest day. Payroll is held for this employee until the weekly schedule gives one.</Status>
    </p>
  );
}

function DateChanges({ changes, canManage, onRemove, employeeName }) {
  if (!changes.length) return null;
  return (
    <ul className="mt-1 space-y-1">
      {changes.map((change) => (
        <li key={change.id} className="flex flex-wrap items-center gap-x-2 text-meta">
          <CalendarDays className="h-3.5 w-3.5 text-tertiary" />
          <span className="font-medium">{formatDateValue(change.schedule_date)}</span>
          <span>{change.shift_id ? `${Number(change.is_rest_day_work) ? "Rest-day work: " : ""}${change.shift_name} (${fmtTime(change.start_time)} – ${fmtTime(change.end_time)})` : "Rest day"}</span>
          {change.reason ? <span className="text-secondary">— {change.reason}</span> : null}
          {canManage && (
            <button onClick={() => onRemove(change)} className="inline-flex items-center text-danger rounded-control font-medium hover:underline max-md:min-h-touch" aria-label={`Undo the temporary change on ${change.schedule_date} for ${employeeName}`}>Undo</button>
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * Report 70: the ordinary week plus any scheduled overtime on top of it (hours only). A 10-hour day here is 8 ordinary
 * hours and 2 authorised overtime hours, never a longer normal day. Overtime is decided in Attendance › Scheduled overtime.
 */
function ScheduledOvertimeLines({ items = [] }) {
  if (!items.length) return null;
  return (
    <div className="mt-1 space-y-0.5">
      {items.map((item) => (
        <p key={item.id} className="text-meta text-secondary">
          + Scheduled overtime {describeScheduledInterval(item)} · {describeScheduledPeriod(item)} · {SCHEDULED_STATE_LABEL[item.state] || item.state}
        </p>
      ))}
    </div>
  );
}

function EmployeeScheduleTable({ employees, canManage, shifts = [], overrides = [], scheduledOvertime = [], onOverride, onRemoveOverride, onReassign, onRemove, onChooseShift, today }) {
  if (!employees.length) return <div className="py-12 text-center text-body-sm text-tertiary">No schedules match the current filters. To add one, choose a shift in the Shifts tab.</div>;
  const changesFor = (employeeId) => overrides.filter((row) => String(row.business_member_id) === String(employeeId));
  const overtimeFor = (employeeId) => scheduledOvertime.filter((item) => String(item.memberId) === String(employeeId) && (item.status === "pending" || isLiveScheduled(item)));
  const nameOf = shiftNamer(shifts);
  return (
    <>
    <div className="space-y-3 lg:hidden">
      {employees.map((employee) => {
        const changes = changesFor(employee.id);
        return <article key={employee.id} className="rounded-panel border p-4">
        <div className="flex flex-wrap items-center gap-2"><h3 className="font-semibold">{employee.name}</h3><MemberStatusBadge status={employee.status} /></div>
        
        <div className="mt-3 text-meta font-semibold text-secondary">Weekly schedule</div>
        {employee.conflict ? <p className="mt-1 text-body-sm text-warning">Overlapping assignments need review before changes.</p> : employee.current ? <div className="mt-1">
          <WeeklySummary assignment={employee.current} nameOf={nameOf} />
          <div className="text-meta text-secondary">{formatDateValue(employee.current.effective_from)} – {formatDateValue(employee.current.effective_until)}</div>
          <NoRestDay assignment={employee.current} />
          <ScheduledOvertimeLines items={overtimeFor(employee.id)} />
        </div> : <p className="mt-1 text-body-sm text-secondary">No weekly schedule</p>}
        {changes.length > 0 && <div className="mt-3"><div className="text-meta font-semibold text-secondary">Temporary changes</div>
          <DateChanges changes={changes} canManage={canManage} onRemove={onRemoveOverride} employeeName={employee.name} />
        </div>}
        {employee.upcoming.length > 0 && <div className="mt-3"><div className="text-meta font-semibold text-secondary">Upcoming weekly schedules</div>
          {employee.upcoming.map((next) => <div key={next.id} className="mt-1 flex flex-wrap items-center justify-between gap-3 text-body-sm"><span>From {formatDateValue(next.effective_from)}: {describeWeek(weekFromAssignment(next), nameOf)}</span>{canManage && <span className="flex gap-3"><button onClick={() => onReassign(next)} className="inline-flex items-center text-accent rounded-control font-medium hover:underline max-md:min-h-touch">Change</button><button onClick={() => onRemove(next)} className="inline-flex items-center text-danger rounded-control font-medium hover:underline max-md:min-h-touch">Remove</button></span>}</div>)}
        </div>}
        {employee.history.length > 0 && <details className="mt-3 text-body-sm"><summary className="cursor-pointer rounded-control max-md:flex max-md:min-h-touch max-md:items-center">{employee.conflict ? "Records to review" : "Past schedules"} ({employee.history.length})</summary>
          {!employee.conflict && <p className="mt-1 text-meta text-secondary">Started schedules are kept for attendance history and cannot be deleted.</p>}
          <ul className="mt-2 space-y-2">{employee.history.map((past) => <li key={past.id} className="border-l-2 pl-2 text-meta">
            <div className="font-medium">{describeWeek(weekFromAssignment(past), nameOf)}</div><div>{formatDateValue(past.effective_from)} – {formatDateValue(past.effective_until)}</div>
            {normalizeDateInputValue(past.effective_until) < normalizeDateInputValue(past.effective_from) && <div className="text-danger">Invalid date range; needs review</div>}
          </li>)}</ul>
        </details>}
        {canManage && <div className="mt-4 flex flex-wrap gap-3 border-t pt-3 text-body-sm">
          {employee.current && !employee.conflict ? <><button onClick={() => onReassign(employee.current)} className="inline-flex items-center text-accent rounded-control font-medium hover:underline max-md:min-h-touch">Change weekly schedule</button>
            <button onClick={() => onOverride(employee.current)} className="inline-flex items-center text-accent rounded-control font-medium hover:underline max-md:min-h-touch">Temporary change</button>
            {normalizeDateInputValue(employee.current.effective_from) >= today && <button onClick={() => onRemove(employee.current)} className="inline-flex items-center text-danger rounded-control font-medium hover:underline max-md:min-h-touch">Remove assignment</button>}</>
            : !employee.conflict && <button onClick={onChooseShift} className="inline-flex items-center text-accent rounded-control font-medium hover:underline max-md:min-h-touch">Choose a shift</button>}
        </div>}
      </article>;
      })}
    </div>
    <div className="relative hidden overflow-x-auto rounded-panel border border-default bg-surface lg:block">
      <table data-ui="table" className="w-full text-left text-cell text-foreground">
        <THead>
          <tr><Th>Employee</Th><Th>Weekly schedule</Th><Th>Temporary &amp; upcoming changes</Th><Th>History</Th><Th>Actions</Th></tr>
        </THead>
        <tbody className="divide-y divide-subtle">
          {employees.map((employee) => {
            const current = employee.current;
            const changes = changesFor(employee.id);
            return <tr key={employee.id}>
              <td className="px-4 py-3 align-top"><div className="font-medium">{employee.name}</div><div className="mt-1"><MemberStatusBadge status={employee.status} /></div></td>
              <td className="px-4 py-3 align-top">
                {employee.conflict ? <span className="text-warning">Overlapping assignments need review before changes.</span> : current ? <>
                  <WeeklySummary assignment={current} nameOf={nameOf} />
                  <div className="text-meta text-secondary">{formatDateValue(current.effective_from)} – {formatDateValue(current.effective_until)}</div>
                  <NoRestDay assignment={current} />
                  <ScheduledOvertimeLines items={overtimeFor(employee.id)} />
                </> : <span className="text-secondary">No weekly schedule</span>}
              </td>
              <td className="px-4 py-3 align-top text-meta">
                <DateChanges changes={changes} canManage={canManage} onRemove={onRemoveOverride} employeeName={employee.name} />
                {employee.upcoming.map((next) => <div key={next.id} className="mt-1">
                  <span className="font-medium">From {formatDateValue(next.effective_from)}:</span> {describeWeek(weekFromAssignment(next), nameOf)}
                  {canManage && <button onClick={() => onReassign(next)} className="ml-2 inline-flex items-center text-accent rounded-control font-medium hover:underline max-md:min-h-touch" aria-label={`Change the weekly schedule starting ${next.effective_from} for ${employee.name}`}>Change</button>}
                  {canManage && <button onClick={() => onRemove(next)} className="ml-2 inline-flex items-center text-danger rounded-control font-medium hover:underline max-md:min-h-touch" aria-label={`Remove the weekly schedule starting ${next.effective_from} for ${employee.name}`}>Remove</button>}
                </div>)}
                {!changes.length && !employee.upcoming.length ? <span className="text-secondary">None</span> : null}
              </td>
              <td className="px-4 py-3 align-top text-meta">
                {employee.history.length ? <details><summary className="cursor-pointer rounded-control max-md:flex max-md:min-h-touch max-md:items-center">{employee.history.length} {employee.conflict ? "record" : "past record"}{employee.history.length === 1 ? "" : "s"}</summary>
                  {!employee.conflict && <p className="mt-1 text-secondary">Started schedules are kept for attendance history and cannot be deleted.</p>}
                  <ul className="mt-2 space-y-2">{employee.history.map((past) => <li key={past.id} className="border-l-2 pl-2">
                    <div className="font-medium">{describeWeek(weekFromAssignment(past), nameOf)}</div><div>{formatDateValue(past.effective_from)} – {formatDateValue(past.effective_until)}</div>
                    {normalizeDateInputValue(past.effective_until) < normalizeDateInputValue(past.effective_from) && <div className="text-danger">Invalid date range; needs review</div>}
                  </li>)}</ul>
                </details> : <span className="text-secondary">None</span>}
              </td>
              <td className="px-4 py-3 align-top text-meta">
                {canManage && current && !employee.conflict ? <div className="flex flex-col items-start gap-2">
                  <button onClick={() => onReassign(current)} className="inline-flex items-center gap-1 text-accent rounded-control font-medium hover:underline max-md:min-h-touch"><ArrowRightLeft className="h-4 w-4" /> Change weekly schedule</button>
                  <button onClick={() => onOverride(current)} className="inline-flex items-center gap-1 text-accent rounded-control font-medium hover:underline max-md:min-h-touch"><CalendarDays className="h-4 w-4" /> Temporary change</button>
                  {normalizeDateInputValue(current.effective_from) >= today && <button onClick={() => onRemove(current)} className="inline-flex items-center gap-1 text-danger rounded-control font-medium hover:underline max-md:min-h-touch"><Trash2 className="h-4 w-4" /> Remove</button>}
                </div> : !current && !employee.conflict && canManage ? <button onClick={onChooseShift} className="inline-flex items-center text-accent rounded-control font-medium hover:underline max-md:min-h-touch">Choose a shift</button> : null}
              </td>
            </tr>;
          })}
        </tbody>
      </table>
    </div>
    </>
  );
}

/* ─── Temporary change (one date or a short range) ───────── */
const MAX_DATE_CHANGE_DAYS = 31;

function daysBetweenInclusive(from, to) {
  const a = new Date(`${from}T00:00:00Z`);
  const b = new Date(`${to}T00:00:00Z`);
  return Math.round((b - a) / 86400000) + 1;
}

function OverrideModal({ open, onClose, onSubmit, assignment, shifts }) {
  const today = getTodayInputValue();
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState("");
  const [mode, setMode] = useState("shift");
  const [shiftId, setShiftId] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAttempted(false);
    setFromDate(today);
    setToDate("");
    setMode("shift");
    setShiftId("");
    setReason("");
    setSaving(false);
  }, [open, today]);

  if (!open || !assignment) return null;
  const endDate = toDate || fromDate;
  const span = fromDate && endDate >= fromDate ? daysBetweenInclusive(fromDate, endDate) : 0;
  const nameOf = shiftNamer(shifts);
  const normalWeek = weekFromAssignment(assignment);
  const normalFor = (date) => {
    const weekday = ((new Date(`${date}T00:00:00Z`).getUTCDay() + 6) % 7) + 1;
    const shift = shifts.find((item) => Number(item.id) === Number(normalWeek[weekday]));
    return shift ? `${shift.name} · ${fmtTime(shift.start_time)} – ${fmtTime(shift.end_time)}` : "Rest day";
  };
  const errors = [];
  if (!fromDate) errors.push("Pick the first date.");
  else if (fromDate < today) errors.push("Past dates cannot be changed.");
  if (toDate && toDate < fromDate) errors.push("\"Until\" must be on or after \"From\".");
  if (span > MAX_DATE_CHANGE_DAYS) errors.push(`A temporary change can cover at most ${MAX_DATE_CHANGE_DAYS} days. For a lasting change, use Change weekly schedule.`);
  if (mode !== "rest" && !shiftId) errors.push("Choose the shift to work, or pick Rest day.");
  // Report 55 (OD-26): work on a rest day is authorised explicitly, with a reason; payroll pays the rest-day rate.
  if (mode === "rest_work" && reason.trim().length < 5) errors.push("Say why the employee works on their rest day (at least 5 characters).");

  const submit = async () => {
    setAttempted(true);
    if (saving || errors.length) return;
    setSaving(true);
    try {
      await onSubmit({
        business_member_id: Number(assignment.business_member_id),
        from_date: fromDate,
        to_date: toDate || undefined,
        ...(mode === "rest" ? { rest_day: true, shift_id: null } : { shift_id: Number(shiftId), ...(mode === "rest_work" ? { rest_day_work: true } : {}) }),
        reason: reason.trim() || undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose(); }} title="Temporary change" size="md">
      <div className="text-body-sm text-foreground">
        <p className="mb-3 text-body-sm text-secondary">
          Replaces {assignment.member_name || `#${assignment.business_member_id}`}'s normal schedule on the dates below only. Afterwards the normal weekly schedule resumes automatically.
        </p>

        <div className="mb-3 rounded-control bg-surface-sunken px-3 py-2 text-meta text-secondary" data-testid="temporary-change-context">
          {span === 1 && fromDate
            ? <>Normal schedule for {formatDateValue(fromDate)}: <span className="font-medium">{normalFor(fromDate)}</span></>
            : <>Normal weekly schedule: <span className="font-medium">{describeWeek(normalWeek, nameOf)}</span></>}
        </div>

        <div className="mb-3 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-body-sm font-medium">From *</label>
            <DatePickerInput value={fromDate} onValueChange={setFromDate} min={today} title="SELECT FIRST DATE" ariaLabel="Temporary change from" className="w-full rounded-control border px-3 py-2 text-body-sm" />
          </div>
          <div>
            <label className="mb-1 block text-body-sm font-medium">Until (optional)</label>
            <DatePickerInput value={toDate} onValueChange={setToDate} min={fromDate || today} title="SELECT LAST DATE" ariaLabel="Temporary change until" className="w-full rounded-control border px-3 py-2 text-body-sm" />
          </div>
        </div>

        <fieldset className="mb-3">
          <legend className="mb-1 block text-body-sm font-medium">{span > 1 ? `For the ${span} selected dates` : "For the selected date"}</legend>
          <div className="flex gap-4 text-body-sm">
            <label className="inline-flex items-center gap-2 max-md:min-h-touch"><input type="radio" name="override-mode" checked={mode === "shift"} onChange={() => setMode("shift")} /> Work a shift</label>
            <label className="inline-flex items-center gap-2 max-md:min-h-touch"><input type="radio" name="override-mode" checked={mode === "rest"} onChange={() => setMode("rest")} /> Rest day</label>
            <label className="inline-flex items-center gap-2 max-md:min-h-touch"><input type="radio" name="override-mode" checked={mode === "rest_work"} onChange={() => setMode("rest_work")} /> Rest-day work</label>
          </div>
          {mode === "rest_work" ? (
            <p className="mt-1 text-meta text-secondary">For a day that is normally the employee's rest day. The day stays their rest day; payroll pays the rest-day rate for the work. A change that makes seven work days in a row is refused unless it is rest-day work.</p>
          ) : null}
        </fieldset>

        {mode !== "rest" ? (
          <div className="mb-3">
            <label className="mb-1 block text-body-sm font-medium">Shift *</label>
            <ShopSelect
              value={shiftId}
              onValueChange={setShiftId}
              options={[
                { value: "", label: "Select a shift" },
                ...shifts.filter((item) => item.status === "active").map((item) => ({ value: item.id, disabled: isLongShift(item), label: `${item.name} (${fmtTime(item.start_time)} – ${fmtTime(item.end_time)})${isLongShift(item) ? " · over 8 paid hours, not available" : ""}` })),
              ]}
              ariaLabel="Shift for the temporary change"
              align="start"
              minPanelWidth={240}
              triggerClassName="min-h-10 w-full rounded-control px-3 py-2 text-body-sm"
              panelClassName="py-1"
            />
          </div>
        ) : null}

        <label htmlFor="override-reason" className="mb-1 block text-body-sm font-medium">Reason{mode === "rest_work" ? " *" : ""}</label>
        <textarea id="override-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} placeholder={mode === "rest_work" ? "Why the employee works on their rest day" : "Optional, e.g. covering inventory day"} className="mb-3 w-full rounded-control border px-3 py-2 text-body-sm" />

        {errors.length ? (
          <p className={`mb-3 text-meta ${attempted ? "text-danger" : "text-secondary"}`} role={attempted ? "alert" : undefined}>{errors[0]}</p>
        ) : null}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-control border px-4 py-2 text-body-sm">Cancel</button>
          <button onClick={submit} disabled={saving} className="business-primary-action inline-flex items-center gap-2 rounded-control bg-accent px-4 py-2 text-body-sm text-on-accent disabled:opacity-40">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save temporary change
          </button>
        </div>
      </div>
    </Dialog>
  );
}

/* ─── Staffing by day (derived from employee schedules) ─── */
function addDaysToInput(value, days) {
  const date = new Date(`${value}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function StaffingView({ today }) {
  const [from, setFrom] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    businessApi.getShiftStaffing({ from, to: addDaysToInput(from, 6) })
      .then((response) => { if (active) setData(response.staffing || null); })
      .catch((err) => { if (active) setError(err?.serverMessage || err?.message || "Could not load who is working."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [from]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-body-sm text-secondary">Who works which shift each day, worked out from everyone's weekly schedule and temporary changes.</p>
        <div className="flex gap-2">
          <button onClick={() => setFrom(addDaysToInput(from, -7))} className="rounded-control border border-default bg-surface px-3 py-1.5 text-body-sm text-foreground hover:border-strong max-md:min-h-touch">Previous week</button>
          <button onClick={() => setFrom(today)} disabled={from === today} className="rounded-control border border-default bg-surface px-3 py-1.5 text-body-sm text-foreground hover:border-strong disabled:opacity-40 max-md:min-h-touch">This week</button>
          <button onClick={() => setFrom(addDaysToInput(from, 7))} className="rounded-control border border-default bg-surface px-3 py-1.5 text-body-sm text-foreground hover:border-strong max-md:min-h-touch">Next week</button>
        </div>
      </div>
      {error ? <div className="rounded-panel border border-danger bg-danger-subtle p-4 text-body-sm text-danger">{error}</div> : null}
      {loading ? <div className="py-8 text-center text-body-sm text-tertiary">Loading…</div> : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {(data?.days || []).map((day) => (
            <section key={day.date} className="rounded-panel border p-3">
              <h3 className="text-body-sm font-semibold">{formatDateValue(day.date)} <span className="font-normal text-secondary">{new Date(`${day.date}T00:00:00Z`).toLocaleDateString("en-PH", { weekday: "long", timeZone: "UTC" })}</span></h3>
              {day.shifts.length === 0 ? <p className="mt-2 text-meta text-secondary">Nobody scheduled.</p> : day.shifts.map((group) => (
                <div key={group.shift.id} className="mt-2">
                  <div className="text-meta font-semibold text-secondary">{group.shift.name} <span className="font-normal text-tertiary">{fmtTime(group.shift.startTime)} – {fmtTime(group.shift.endTime)}</span></div>
                  <ul className="mt-0.5 text-body-sm">{group.people.map((person) => <li key={person.memberId}>{person.name}</li>)}</ul>
                </div>
              ))}
              {day.resting.length ? <p className="mt-2 text-meta text-secondary">Rest: {day.resting.map((person) => person.name).join(", ")}</p> : null}
              {day.needsReview.length ? <p className="mt-1 text-meta text-warning">Needs review (overlapping schedules): {day.needsReview.map((person) => person.name).join(", ")}</p> : null}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────── */
export default function HRShifts() {
  const auth = useBusinessAuth();
  const authRole = String(auth?.role || "").toUpperCase();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shifts, setShifts] = useState([]);
  const [scheduleDefaults, setScheduleDefaults] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [scheduledOvertime, setScheduledOvertime] = useState([]);
  const [overriding, setOverriding] = useState(null);
  const [deliveryHours, setDeliveryHours] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [assignableMembers, setAssignableMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const readTab = (value) => (["assignments", "staffing", "requests", "overtime"].includes(value) ? value : "shifts");
  const [tab, setTab] = useState(readTab(searchParams.get("tab")));
  const [query, setQuery] = useState("");
  const [memberFilter, setMemberFilter] = useState(searchParams.get("memberId") || "");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [reassigning, setReassigning] = useState(null);
  const [reassignShiftId, setReassignShiftId] = useState(null);
  const [pendingRequests, setPendingRequests] = useState(null);
  const [notice, setNotice] = useState("");
  const [confirming, setConfirming] = useState(null);
  const [failure, setFailure] = useState(null);
  const loadScheduleRequests = useCallback((params) => businessApi.getScheduleRequests(params), []);
  const decideScheduleRequest = useCallback((id, decision, body) => businessApi.decideScheduleRequest(id, decision, body), []);
  useEffect(() => {
    businessApi.getScheduleRequests({ status: "pending" }).then((res) => setPendingRequests((res?.requests || []).length)).catch(() => {});
  }, []);
  const canManageAssignments =
    authRole === "OWNER" ||
    auth?.hasPermission?.("hr:shifts:manage") ||
    auth?.hasPermission?.("hr:manage");
  const canReadShifts =
    canManageAssignments ||
    authRole === "OWNER" ||
    auth?.hasPermission?.("hr:shifts:read") ||
    auth?.hasPermission?.("hr:read");
  const canApproveOvertime = authRole === "OWNER" || auth?.hasPermission?.("hr:overtime:approve");
  const canAssignOvertime = authRole === "OWNER" || auth?.hasPermission?.("hr:overtime:assign");
  const canSeeScheduledOvertime = canApproveOvertime || canAssignOvertime;
  const myMemberId = Number(auth?.currentBusiness?.membership_id || 0);
  const [schedulingOvertime, setSchedulingOvertime] = useState(false);
  const [scheduledReload, setScheduledReload] = useState(0);
  const [pendingScheduled, setPendingScheduled] = useState(null);
  const shiftTabs = SHIFT_TABS.filter((item) => item.key !== "overtime" || canSeeScheduledOvertime);
  const activeTab = tab === "overtime" && !canSeeScheduledOvertime ? "shifts" : tab;
  const tabRow = useSelectedTabInView('[aria-selected="true"]', `${activeTab}|${pendingScheduled ?? ""}|${pendingRequests ?? ""}`);
  const today = getTodayInputValue();

  useEffect(() => {
    const nextTab = readTab(searchParams.get("tab"));
    const nextMemberFilter = searchParams.get("memberId") || "";
    setTab((current) => (current === nextTab ? current : nextTab));
    setMemberFilter((current) => (current === nextMemberFilter ? current : nextMemberFilter));
  }, [searchParams]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", tab);
    if (memberFilter) nextParams.set("memberId", memberFilter);
    else nextParams.delete("memberId");
    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [memberFilter, searchParams, setSearchParams, tab]);

  // Advisory only. Without delivery access (or rules) there is simply no advice.
  useEffect(() => {
    let active = true;
    getBusinessDispatchWindows()
      .then((response) => {
        const data = response?.data;
        if (active && data?.available) {
          setDeliveryHours({ receivingHours: data.receivingHours || [], riderIds: new Set((data.riderMemberIds || []).map(String)) });
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);
  const adviceFor = useCallback((memberId, shift) => shiftDeliveryAdvice({
    shift,
    receivingHours: deliveryHours?.receivingHours || [],
    isRider: Boolean(deliveryHours?.riderIds?.has(String(memberId))),
  }), [deliveryHours]);

  const load = useCallback(async () => {
    if (!canReadShifts) return;
    try {
      setLoading(true);
      setError("");
      const [s, a, o] = await Promise.all([
        businessApi.getShifts(),
        businessApi.getShiftAssignments(memberFilter ? { memberId: memberFilter } : {}),
        businessApi.getShiftOverrides({ from: getTodayInputValue(), ...(memberFilter ? { memberId: memberFilter } : {}) }),
      ]);
      setShifts(s.shifts || []);
      setScheduleDefaults(s.defaults || null);
      setAssignments(a.assignments || []);
      setOverrides(o.overrides || []);
      // Report 70: scheduled overtime on top of the weekly schedules (hours only); absent when it cannot be read.
      businessApi.getScheduledOvertime(memberFilter ? { memberId: memberFilter } : {})
        .then((res) => setScheduledOvertime(res?.authorizations || []))
        .catch(() => setScheduledOvertime([]));
    } catch (err) {
      setError(err?.message || "Failed to load shift data.");
    } finally {
      setLoading(false);
    }
  }, [canReadShifts, memberFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;

    if (!canManageAssignments) {
      setAssignableMembers([]);
      return undefined;
    }

    (async () => {
      try {
        const response = await businessApi.getShiftAssignableMembers();
        if (!cancelled) setAssignableMembers(response.members || []);
      } catch {
        if (!cancelled) setAssignableMembers([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canManageAssignments]);

  const showSuccess = useCallback((title, text) => setNotice(`${title}. ${text}`), []);
  const showFailure = (title, err, fallback) => setFailure({ title, text: plainError(err, fallback) });
  const employeeOf = (row) => row?.member_name || `Employee #${row?.business_member_id}`;

  const handleSubmitShift = async (form) => {
    try {
      if (editing) {
        await businessApi.updateShift(editing.id, form);
        showSuccess("Shift updated", `${editing.name} was updated.`);
      } else {
        await businessApi.createShift(form);
        showSuccess("Shift created", `${form.name} is now available for assignment.`);
      }
      setShowForm(false);
      setEditing(null);
      await load();
    } catch (err) {
      showFailure("Shift not saved", err, "The shift could not be saved. Try again.");
    }
  };

  // C3: an operational definition stops being assignable. The server refuses while employees are still on it.
  const handleDeactivate = (shift) => setConfirming({
    title: `Deactivate ${shift.name}?`,
    tone: "danger",
    confirmLabel: "Deactivate shift",
    cancelLabel: "Keep shift active",
    failureTitle: "Shift not deactivated",
    facts: [{ label: "Shift", value: `${shift.name} · ${fmtTime(shift.start_time)} – ${fmtTime(shift.end_time)}` }],
    consequence: "It stays in history but can no longer be assigned. Nobody is moved off it automatically.",
    run: async () => {
      await businessApi.deactivateShift(shift.id);
      showSuccess("Shift deactivated", `${shift.name} is no longer assignable.`);
      await load();
    },
  });

  // APP-HR-080: an archived shift can be made assignable again; a shift nothing has ever used can be deleted.
  const handleRestore = (shift) => setConfirming({
    title: `Restore ${shift.name}?`,
    confirmLabel: "Restore shift",
    cancelLabel: "Keep inactive",
    failureTitle: "Shift not restored",
    facts: [{ label: "Shift", value: `${shift.name} · ${fmtTime(shift.start_time)} – ${fmtTime(shift.end_time)}` }],
    consequence: "It can be assigned to employees again. Nobody is put on it automatically.",
    run: async () => {
      await businessApi.restoreShift(shift.id);
      showSuccess("Shift restored", `${shift.name} can be assigned again.`);
      await load();
    },
  });
  const handleDelete = (shift) => setConfirming({
    title: `Delete ${shift.name}?`,
    tone: "danger",
    confirmLabel: "Delete shift",
    cancelLabel: "Keep shift",
    failureTitle: "Shift not deleted",
    facts: [{ label: "Shift", value: `${shift.name} · ${fmtTime(shift.start_time)} – ${fmtTime(shift.end_time)}` }],
    consequence: "This shift has never been used, so it is removed for good and its name becomes free. This cannot be undone. A shift that has been on a schedule or attendance cannot be deleted; it is deactivated instead.",
    run: async () => {
      await businessApi.deleteShift(shift.id);
      showSuccess("Shift deleted", `${shift.name} was removed.`);
      await load();
    },
  });

  const handleAssign = async (data) => {
    try {
      await businessApi.assignShift(assigning.id, data);
      setAssigning(null);
      showSuccess("Schedule assigned", "The employee's schedule was updated.");
      await load();
    } catch (err) {
      // The employee already has a shift on those dates. What the person usually
      // wants is to switch it, which Change shift does; offer it directly.
      if (err?.serverCode === "SHIFT_ASSIGNMENT_OVERLAP") {
        const memberName = assignableMembers.find((member) => String(member.id) === String(data.business_member_id))?.name || "This employee";
        const conflict = assignments.find((row) => Number(row.id) === Number(err.serverDetails?.conflictingAssignmentId));
        setFailure({
          title: `${memberName} already has a shift`,
          text: plainError(err, "This employee already has a shift on those dates.").replace(/^This employee/, memberName),
          action: conflict ? {
            label: `Change ${memberName}'s shift`,
            run: () => {
              const nextShiftId = assigning?.id;
              setAssigning(null);
              setReassignShiftId(nextShiftId);
              setReassigning(conflict);
            },
          } : null,
        });
        return;
      }
      showFailure("Schedule not assigned", err, "The schedule could not be assigned. Try again.");
    }
  };

  const handleReassign = async (data) => {
    try {
      await businessApi.reassignShiftAssignment(reassigning.id, data);
      setReassigning(null);
      setReassignShiftId(null);
      showSuccess("Weekly schedule changed", "The employee's schedule was updated.");
      await load();
    } catch (err) {
      showFailure("Schedule not changed", err, "The schedule could not be changed. Try again.");
    }
  };

  const handleSetOverride = async (data) => {
    try {
      await businessApi.setShiftOverride(data);
      setOverriding(null);
      showSuccess("Temporary change saved", "The normal weekly schedule resumes after it.");
      await load();
    } catch (err) {
      showFailure("Temporary change not saved", err, "The temporary change could not be saved. Try again.");
    }
  };

  // C2: one date goes back to the employee's normal week.
  const handleRemoveOverride = (override) => setConfirming({
    title: "Undo this temporary change?",
    tone: "danger",
    confirmLabel: "Undo change",
    cancelLabel: "Keep the change",
    failureTitle: "Not undone",
    facts: [
      { label: "Employee", value: employeeOf(override) },
      { label: "Date", value: formatDateValue(override.schedule_date) },
    ],
    consequence: "That date follows the employee's normal weekly schedule again.",
    run: async () => {
      await businessApi.removeShiftOverride(override.id);
      showSuccess("Temporary change undone", "That date follows the normal weekly schedule again.");
      await load();
    },
  });

  // C3: the employee is left without this schedule from its start date; the server refuses once it has started.
  const handleUnassign = (assignment) => setConfirming({
    title: `Remove ${employeeOf(assignment)}'s ${isFutureDate(assignment.effective_from, today) ? "upcoming" : "current"} schedule?`,
    tone: "danger",
    confirmLabel: "Remove assignment",
    cancelLabel: "Keep assignment",
    failureTitle: "Assignment not removed",
    facts: [
      { label: "Employee", value: employeeOf(assignment) },
      { label: "Shift", value: assignment.shift_name },
      { label: "Effective from", value: formatDateValue(assignment.effective_from) },
    ],
    consequence: "They have no schedule from that date until a new one is assigned. A schedule that has already started cannot be removed; change it instead.",
    run: async () => {
      await businessApi.unassignShift(assignment.id);
      showSuccess("Assignment removed", "The schedule assignment was removed.");
      await load();
    },
  });

  const filteredShifts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return shifts;
    return shifts.filter((shift) => {
      const haystack = [shift.name, shift.code, shift.start_time, shift.end_time, shift.status]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [query, shifts]);

  const fmtTime = (t) => {
    if (!t) return "—";
    const parts = String(t).split(":");
  const h = Number(parts[0]);
  const m = parts[1];
  const am = h < 12 ? "AM" : "PM";
  return `${h % 12 || 12}:${m} ${am}`;
  };

  const memberOptions = useMemo(() => {
    if (canManageAssignments && assignableMembers.length > 0) return assignableMembers;
    const byId = new Map();
    assignments.forEach((assignment) => {
      if (!assignment?.business_member_id || byId.has(assignment.business_member_id)) return;
      byId.set(assignment.business_member_id, {
        id: assignment.business_member_id,
        name: assignment.member_name || `#${assignment.business_member_id}`,
        status: assignment.member_status || "unknown",
        currentShiftName: assignment.shift_name || null,
      });
    });
    return Array.from(byId.values());
  }, [assignableMembers, assignments, canManageAssignments]);

  const employeeSchedules = useMemo(() => groupShiftAssignments(
    assignments, [], today, query, memberFilter,
  ), [assignments, today, query, memberFilter]);

  const headerActions = canManageAssignments && activeTab === "shifts" ? (
    <Button variant="primary" icon={Plus} onClick={() => { setEditing(null); setShowForm(true); }}>New shift</Button>
  ) : activeTab === "overtime" && canAssignOvertime ? (
    <Button variant="primary" icon={Plus} onClick={() => setSchedulingOvertime(true)}>Schedule overtime</Button>
  ) : null;

  if (!canReadShifts) {
    return (
      <div className="space-y-6">
        <PageHeader title="Shifts & Scheduling" />
        <Notice tone="warning" title="You can't open this page">
          Shift definitions and employee schedules are visible only to people who manage HR schedules. Ask the owner if you need access.
        </Notice>
      </div>
    );
  }

  return (
    <div className="space-y-6">
    <PageHeader
      title="Shifts & Scheduling"
      description={canManageAssignments
        ? "Define shifts, set each employee's weekly schedule, and make temporary changes when plans change."
        : "Shift definitions and employee schedules. You can view them but not change them."}
      actions={headerActions}
      navigation={
        <div ref={tabRow} role="tablist" aria-label="Shifts and scheduling sections" className="-mb-px flex gap-6 overflow-x-auto overflow-y-hidden border-b border-subtle">
          {shiftTabs.map((item) => (
            <button
              key={item.key}
              type="button"
              role="tab"
              data-ui="button"
              aria-selected={activeTab === item.key}
              onClick={() => setTab(item.key)}
              className={`relative inline-flex h-10 shrink-0 items-center gap-1.5 text-body-sm font-medium max-md:h-touch after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:rounded-full ${focusRing} ${activeTab === item.key ? "text-foreground after:bg-accent" : "text-secondary after:bg-transparent hover:text-foreground"}`}
            >
              {item.label}
              {item.key === "requests" && pendingRequests ? <span className="text-meta tabular-nums text-tertiary">{pendingRequests}</span> : null}
              {item.key === "overtime" && pendingScheduled ? <span className="text-meta tabular-nums text-tertiary">{pendingScheduled}</span> : null}
            </button>
          ))}
        </div>
      }
    />
    {notice ? <Notice tone="success" role="status">{notice}</Notice> : null}
    <div className="space-y-4">

      {activeTab !== "staffing" && activeTab !== "requests" && activeTab !== "overtime" ? <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_220px]">
        <SearchInput
          label={tab === "shifts" ? "Search shifts" : "Search assignments"}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={tab === "shifts" ? "Search shifts..." : "Search assignments..."}
        />
        {tab === "assignments" ? (
          <ShopSelect
            value={memberFilter}
            onValueChange={setMemberFilter}
            options={[
              { value: "", label: "All employees" },
              ...memberOptions.map((member) => ({ value: member.id, label: buildMemberOptionLabel(member) })),
            ]}
            ariaLabel="Filter shift assignments by employee"
            align="start"
            minPanelWidth={220}
            triggerClassName="min-h-10 w-full rounded-control px-3 py-2 text-body-sm max-md:min-h-[44px]"
            panelClassName="py-1"
          />
        ) : null}
      </div> : null}

      {error ? (
        <div className="rounded-panel border border-danger bg-danger-subtle p-4 text-body-sm text-danger">
          {error}
        </div>
      ) : null}

      {activeTab === "overtime" ? (
        <section aria-label="Scheduled overtime" className="space-y-3">
          <p className="max-w-[90ch] text-body-sm text-secondary">
            Overtime planned in advance on top of an employee's weekly schedule.{" "}
            {canApproveOvertime
              ? "You can propose and approve it."
              : "You can propose it; someone with overtime approval decides."}{" "}
            Approval authorises the hours. Only time actually worked inside them is paid.
          </p>
          <ScheduledOvertimePanel
            canApprove={canApproveOvertime}
            myMemberId={myMemberId}
            reloadKey={scheduledReload}
            onPendingCount={setPendingScheduled}
            onNotice={setNotice}
          />
          {schedulingOvertime ? (
            <ScheduleOvertimeDialog
              canApprove={canApproveOvertime}
              employees={memberOptions
                .filter((member) => Number(member.id) !== myMemberId && !["inactive", "separated", "removed"].includes(String(member.status || "").toLowerCase()))
                .map((member) => ({ id: member.id, name: member.name || `#${member.id}` }))}
              onClose={() => setSchedulingOvertime(false)}
              onSaved={(created) => {
                setSchedulingOvertime(false);
                setScheduledReload((key) => key + 1);
                setNotice(created?.status === "approved"
                  ? "Scheduled overtime approved. The employee sees it in My HR."
                  : "Scheduled overtime sent for approval. It shows under Waiting for approval until someone with overtime approval decides.");
              }}
            />
          ) : null}
        </section>
      ) : activeTab === "requests" ? (
        <div className="space-y-2">
          <p className="text-body-sm text-secondary">
            Employees ask for a temporary change (some dates, then their usual week resumes) or a new weekly schedule from a date. Approving applies it to their schedule; rejecting leaves it as it is.
          </p>
          <RequestReviewList
            load={loadScheduleRequests}
            decide={decideScheduleRequest}
            onPendingCount={setPendingRequests}
            canDecide={canManageAssignments}
            kind="schedule change"
            emptyText="No schedule requests are waiting."
            approveText={(request) => `${request.memberName || "This employee"}: ${describeScheduleRequest(request)}. Their schedule changes when you approve.`}
            summarize={(request) => (
              <div className="text-foreground">
                <span className="font-medium">{request.changeType === "temporary" ? "Temporary change" : "New weekly schedule"}</span>
                {" — "}{describeScheduleRequest(request)}
              </div>
            )}
          />
        </div>
      ) : activeTab === "staffing" ? (
        <StaffingView today={today} />
      ) : loading ? (
        <div className="py-12 text-center text-body-sm text-tertiary">Loading shift data...</div>
      ) : activeTab === "shifts" ? (
        filteredShifts.length === 0 ? (
          <div className="py-12 text-center text-body-sm text-tertiary">
            {query ? "No shifts match the current search." : "No shifts defined yet."}
          </div>
        ) : (
          <div className="relative overflow-x-auto rounded-panel border border-default bg-surface">
            <table data-ui="table" className="w-full text-left text-cell text-foreground">
              <THead>
                <tr>
                  <Th>Shift</Th>
                  <Th className="max-md:hidden">Time</Th>
                  <Th className="max-md:hidden">Break</Th>
                  <Th className="max-md:hidden">Paid time a day</Th>
                  <Th className="max-md:hidden">Lateness grace</Th>
                  <Th className="text-center max-md:hidden">Assigned</Th>
                  <Th>Status</Th>
                  <Th className="text-center">Actions</Th>
                </tr>
              </THead>
              <tbody className="divide-y divide-subtle">
                {filteredShifts.map((s) => (
                  <tr key={s.id} className="group">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 font-medium">
                        <Timer className="h-4 w-4 text-tertiary" />
                        {s.name}
                        {s.code && <span className="text-meta text-tertiary">({s.code})</span>}
                      </div>
                      {s.is_overnight ? <span className="text-meta text-amber-500">🌙 Overnight</span> : null}
                      {/* Phones: the times and the paid day under the name, so the row needs no sideways scroll. */}
                      <span className="mt-0.5 block text-meta text-secondary md:hidden">
                        {fmtTime(s.start_time)} – {fmtTime(s.end_time)}
                        {s.paid_time ? ` · ${hoursText(s.paid_time.compensableMinutes)} paid` : ""}
                        {Number(s.break_minutes) > 0 ? ` · ${s.break_minutes} min break, ${Number(s.break_paid || 0) === 1 ? "paid" : "unpaid"}` : " · no break recorded"}
                        {` · ${s.assigned_count} assigned`}
                      </span>
                      {s.paid_time?.notes?.includes("PAID_TIME_ABOVE_NORMAL_HOURS") ? (
                        <span className="mt-0.5 flex items-start gap-1 text-meta text-warning md:hidden">
                          <AlertTriangle aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          {s.paid_time.breakMinutes ? "More than 8 paid hours a day" : "No break recorded: all of it is paid as regular time"}. Not available for new schedules.
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 max-md:hidden">
                      <span className="inline-flex items-center gap-1 text-meta">
                        <Clock className="h-3.5 w-3.5 text-tertiary" />
                        {fmtTime(s.start_time)} – {fmtTime(s.end_time)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-secondary max-md:hidden">
                      {Number(s.break_minutes) > 0 ? `${s.break_minutes} min at ${fmtTime(s.break_start_time)}, ${Number(s.break_paid || 0) === 1 ? "paid" : "unpaid"}` : "None recorded"}
                    </td>
                    <td className="px-4 py-3 max-md:hidden">
                      <span className="tabular-nums">{s.paid_time ? hoursText(s.paid_time.compensableMinutes) : "—"}</span>
                      {s.paid_time?.notes?.includes("PAID_TIME_ABOVE_NORMAL_HOURS") ? (
                        <span className="mt-0.5 flex items-start gap-1 text-meta text-warning">
                          <AlertTriangle aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          {s.paid_time.breakMinutes ? "More than 8 paid hours a day" : "No break recorded: all of it is paid as regular time"}. Not available for new schedules.
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-secondary max-md:hidden">
                      {s.grace_period_minutes === null || s.grace_period_minutes === undefined
                        ? `${scheduleDefaults?.graceMinutes ?? "—"} min (attendance pay policy)`
                        : `${s.grace_period_minutes} min (this shift's own)`}
                    </td>
                    <td className="px-4 py-3 text-center max-md:hidden">{s.assigned_count}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={s.status} />
                      {s.status === "active" && isLongShift(s) ? <span className="mt-0.5 block text-meta text-secondary">Existing schedules only</span> : null}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {canManageAssignments ? (
                        <div className="inline-flex gap-1 max-md:grid max-md:grid-cols-2">
                          <button onClick={() => setAssigning(s)} disabled={isLongShift(s)} className="inline-flex h-control-sm w-8 items-center justify-center rounded-control text-secondary hover:bg-hover hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40 max-md:min-h-touch max-md:min-w-11" title={isLongShift(s) ? "Not available: more than 8 paid hours a day" : "Assign shift"} aria-label={isLongShift(s) ? `Assign ${s.name} (not available: more than 8 paid hours a day)` : `Assign ${s.name}`}>
                            <UserPlus className="h-4 w-4 text-tertiary" />
                          </button>
                          <button onClick={() => { setEditing(s); setShowForm(true); }} className="inline-flex h-control-sm w-8 items-center justify-center rounded-control text-secondary hover:bg-hover hover:text-foreground max-md:min-h-touch max-md:min-w-11" title="Edit shift">
                            <Edit2 className="h-4 w-4 text-tertiary" />
                          </button>
                          {s.status === "active" ? (
                            <button onClick={() => handleDeactivate(s)} className="inline-flex h-control-sm w-8 items-center justify-center rounded-control text-secondary hover:bg-hover hover:text-foreground max-md:min-h-touch max-md:min-w-11" title="Deactivate shift" aria-label={`Deactivate ${s.name}`}>
                              <XCircle className="h-4 w-4 text-red-400" />
                            </button>
                          ) : (
                            <button onClick={() => handleRestore(s)} className="inline-flex h-control-sm w-8 items-center justify-center rounded-control text-secondary hover:bg-hover hover:text-foreground max-md:min-h-touch max-md:min-w-11" title="Restore shift" aria-label={`Restore ${s.name}`}>
                              <RotateCcw className="h-4 w-4 text-tertiary" />
                            </button>
                          )}
                          {Number(s.is_used) === 0 ? (
                            <button onClick={() => handleDelete(s)} className="inline-flex h-control-sm w-8 items-center justify-center rounded-control text-secondary hover:bg-hover hover:text-foreground max-md:min-h-touch max-md:min-w-11" title="Delete shift (never used)" aria-label={`Delete ${s.name}`}>
                              <Trash2 className="h-4 w-4 text-red-400" />
                            </button>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-meta text-tertiary">Read only</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <EmployeeScheduleTable
          employees={employeeSchedules}
          scheduledOvertime={scheduledOvertime}
          canManage={canManageAssignments}
          shifts={shifts}
          overrides={overrides}
          onOverride={setOverriding}
          onRemoveOverride={handleRemoveOverride}
          onReassign={setReassigning}
          onRemove={handleUnassign}
          onChooseShift={() => setTab("shifts")}
          today={today}
        />
      )}

      <ShiftFormModal
        open={showForm}
        onClose={() => { setShowForm(false); setEditing(null); }}
        onSubmit={handleSubmitShift}
        initial={editing}
        defaultGrace={scheduleDefaults?.graceMinutes}
        normalDayMinutes={scheduleDefaults?.normalDayMinutes ?? 480}
      />
      <AssignModal
        open={!!assigning}
        onClose={() => setAssigning(null)}
        onSubmit={handleAssign}
        shift={assigning}
        shifts={shifts}
        members={assignableMembers}
        defaults={scheduleDefaults}
        adviceFor={adviceFor}
      />
      <ReassignModal
        open={!!reassigning}
        onClose={() => { setReassigning(null); setReassignShiftId(null); }}
        onSubmit={handleReassign}
        assignment={reassigning}
        shifts={shifts}
        initialShiftId={reassignShiftId}
        adviceFor={adviceFor}
      />
      <OverrideModal
        open={!!overriding}
        onClose={() => setOverriding(null)}
        onSubmit={handleSetOverride}
        assignment={overriding}
        shifts={shifts}
      />
      {confirming ? (
        <ConfirmDialog
          title={confirming.title}
          tone={confirming.tone}
          facts={confirming.facts}
          consequence={confirming.consequence}
          confirmLabel={confirming.confirmLabel}
          cancelLabel={confirming.cancelLabel}
          failureTitle={confirming.failureTitle}
          onClose={() => setConfirming(null)}
          onConfirm={async () => { await confirming.run(); setConfirming(null); }}
        />
      ) : null}
      {failure ? (
        <Dialog
          open
          size="sm"
          onOpenChange={(next) => { if (!next) setFailure(null); }}
          title={failure.title}
          footer={
            <>
              <Button onClick={() => setFailure(null)}>Close</Button>
              {failure.action ? <Button variant="primary" onClick={() => { const run = failure.action.run; setFailure(null); run(); }}>{failure.action.label}</Button> : null}
            </>
          }
        >
          <p role="alert" className="text-body-sm text-secondary">{failure.text}</p>
        </Dialog>
      ) : null}
    </div>
    </div>
  );
}
